import type { MouseEvent } from "react";
import { useCallback, useState } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { ChevronBackwardIcon } from "@jti/brand-icons/chevron-backward";
import { useFeatureFlags } from "@jti/commerce";
import { Button } from "@jti/core-components/components/Button";
import { DEPRECATED_IconButton } from "@jti/ui/components/DEPRECATED_IconButton";
import { Dialog } from "@jti/ui/components/Dialog";
import { Typography } from "@jti/ui/components/Typography";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import styles from "./BackToCartButton.module.scss";

type BackToCartButtonProps = {
  variant?: "default" | "paymentDetails";
};

export const BackToCartButton = ({ variant = "default" }: BackToCartButtonProps) => {
  const { __ } = useTranslations();
  const config = useConfig();
  const { ffCheckoutV2EnableIteration2 } = useFeatureFlags();

  const [isWarningDialogOpen, setIsWarningDialogOpen] = useState(false);

  const onBackToCartClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (!ffCheckoutV2EnableIteration2) {
        // Preserve standard behavior of the link when the feature flag is disabled
        return;
      }

      event.preventDefault();

      setIsWarningDialogOpen(true);
    },
    [ffCheckoutV2EnableIteration2]
  );

  const closeDialog = useCallback(() => {
    setIsWarningDialogOpen(false);
  }, []);

  return (
    <>
      <DEPRECATED_IconButton
        className={variant === "paymentDetails" ? styles.paymentDetailsBackToCartButton : undefined}
        icon={ChevronBackwardIcon}
        href={config.CART_CHECKOUT_PAGE_URL}
        onClick={onBackToCartClick}
      >
        {__("cart.checkout.go_back_label")}
      </DEPRECATED_IconButton>

      {ffCheckoutV2EnableIteration2 && (
        <Dialog isOpened={isWarningDialogOpen} onClose={closeDialog}>
          <div className={styles.dialogContainer}>
            <Typography variant="h3">{__("cart.checkout.back_to_cart_warning_dialog.heading")}</Typography>
            <Typography variant="body2" weight="light" className={styles.warningMessage}>
              {__("cart.checkout.back_to_cart_warning_dialog.warning_message")}
            </Typography>
            <div className={styles.dialogFooter}>
              <Button variant="secondary" href={config.CART_CHECKOUT_PAGE_URL}>
                {__("cart.checkout.back_to_cart_warning_dialog.cancel_button")}
              </Button>
              <Button variant="primary" onClick={closeDialog}>
                {__("cart.checkout.back_to_cart_warning_dialog.submit_button")}
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
};
